/* eslint-disable react-hooks/exhaustive-deps */
/* ============================================================
   แผนโหลดสินค้า / การจองเรือ — หน้าเพิ่ม/แก้ไข

   1 Job Order มีได้หลายรอบโหลด/หลายตู้ จึงบันทึกเป็นรายการแยกกัน
============================================================ */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card, Form, Row, Col, Input, Select, DatePicker, InputNumber,
  Button, Flex, Space, Typography, Table, Popconfirm, message,
} from "antd";
import { SaveFilled, PlusOutlined, CalculatorOutlined } from "@ant-design/icons";
import { RiDeleteBin5Line } from "react-icons/ri";
import dayjs from "dayjs";

import ShipmentPlanService from "../../../service/ERP/ShipmentPlan.service";
import ModalJobOrder from "../../../components/modal/ERP/job-order/ModalJobOrder";
import useGoBack from "../../../hooks/useGoBack";
import { BOOKING_STATUS, FREIGHT_TERMS, CONTAINER_TYPES } from "./model";

const spService = ShipmentPlanService();
const gotoFrom = "/ERP/shipment-plan";

/* ช่องวันที่ในฟอร์มเป็น dayjs แต่ฝั่ง API ต้องการ YYYY-MM-DD */
const toDay = (v) => (v ? dayjs(v) : null);
const fmtDay = (v) => (v ? dayjs(v).format("YYYY-MM-DD") : null);

const DATE_FIELDS = ["cy_date", "load_date", "rt_date", "etd", "eta", "first_return"];

export default function ShipmentPlanManage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = location.state || { config: null };
  const goBack = useGoBack(gotoFrom);

  const [form] = Form.useForm();
  const [openJo, setOpenJo] = useState(false);
  /* 1 booking มีได้หลาย Job / หลาย Invoice (เช่น 2 invoice โหลดรวมกัน 1 ตู้) */
  const [jobs, setJobs] = useState([]);
  /* โหมดดูอย่างเดียว — ปิดการแก้ไขทั้งฟอร์ม หน่อนปุ่มบันทึก/เพิ่ม-ลบแถว */
  const isView = config?.action === "view";
  const isCreate = !isView && config?.action !== "edit";

  useEffect(() => {
    if (isCreate) {
      form.setFieldsValue({ booking_status: "ยังไม่จองเรือ", freight: "PREPAID" });
      return;
    }
    spService
      .get(config.code)
      .then((res) => {
        const d = res.data?.data || {};
        const values = { ...d };
        DATE_FIELDS.forEach((f) => {
          values[f] = toDay(d[f]);
        });
        form.setFieldsValue(values);
        setJobs(d.jobs || []);
      })
      .catch(() => message.error("โหลดข้อมูลแผนโหลดไม่สำเร็จ"));
  }, []);

  const handleChooseJo = (record) => {
    if (!record?.jocode) return;
    if (jobs.some((j) => j.jocode === record.jocode)) {
      message.warning(`Job ${record.jocode} อยู่ในรายการแล้ว`);
      return;
    }
    setJobs((prev) => [
      ...prev,
      {
        jocode: record.jocode,
        cuscode: record.cuscode,
        cusname: record.cusname,
        brand: record.brand,
        inv_code: record.inv_code || null,
        fcl: null,
      },
    ]);
    /* Job แรกใช้เป็นข้อมูลหลักของ booking */
    if (jobs.length === 0) {
      form.setFieldsValue({
        cuscode: record.cuscode,
        cusname: record.cusname,
        etd: form.getFieldValue("etd") || toDay(record.etd),
      });
    }
  };

  const updateJob = (index, field, value) =>
    setJobs((prev) => prev.map((j, i) => (i === index ? { ...j, [field]: value } : j)));

  const removeJob = (index) => setJobs((prev) => prev.filter((_, i) => i !== index));

  /* คำนวณจำนวนตู้จากมาตรฐานกล่องต่อตู้ของสินค้าแต่ละรายการ */
  const handleEstimateFcl = () => {
    const codes = jobs.map((j) => j.jocode).filter(Boolean);
    if (codes.length === 0) {
      message.warning("กรุณาเลือก Job Order ก่อน");
      return;
    }
    spService
      .estimateFcl(codes)
      .then((res) => {
        const d = res.data?.data || {};
        form.setFieldValue("fcl", d.fcl);
        setJobs((prev) =>
          prev.map((j) => ({
            ...j,
            fcl: (d.jobs || []).find((x) => x.jocode === j.jocode)?.fcl ?? j.fcl,
          }))
        );
        if ((d.missing || []).length > 0) {
          message.warning(
            `มี ${d.missing.length} รายการที่ยังไม่ได้ตั้งมาตรฐานกล่อง/ตู้ ในทะเบียน Packing Goods`
          );
        } else {
          message.success(`คำนวณได้ ${d.fcl} ตู้`);
        }
      })
      .catch((err) =>
        message.error(err?.response?.data?.message || "คำนวณจำนวนตู้ไม่สำเร็จ")
      );
  };

  const handleSave = () => {
    form.validateFields().then((v) => {
      if (jobs.length === 0) {
        message.warning("กรุณาเลือก Job Order อย่างน้อย 1 รายการ");
        return;
      }
      const payload = { ...v, id: config?.code, jobs };
      DATE_FIELDS.forEach((f) => {
        payload[f] = fmtDay(v[f]);
      });

      const action = isCreate ? spService.create(payload) : spService.update(payload);
      action
        .then((res) => {
          message.success(res.data?.message || "บันทึกเรียบร้อย");
          navigate(gotoFrom, { replace: true });
        })
        .catch((err) =>
          message.error(err?.response?.data?.message || "บันทึกไม่สำเร็จ")
        );
    });
  };

  return (
    <Space direction="vertical" size="middle" style={{ display: "flex" }}>
      <Card>
        <Flex align="center" justify="space-between">
          <Typography.Title level={3} className="m-0 !text-zinc-800">
            {config?.title || "แผนโหลดสินค้า"}
          </Typography.Title>
          <Space>
            <Button onClick={goBack}>กลับ</Button>
            {!isView && (
              <Button type="primary" icon={<SaveFilled />} onClick={handleSave}>
                บันทึก
              </Button>
            )}
          </Space>
        </Flex>
      </Card>

      <Card>
        <Form form={form} layout="vertical" autoComplete="off" disabled={isView}>
          <Form.Item name="cuscode" hidden><Input /></Form.Item>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="ลูกค้า" name="cusname">
                <Input placeholder="ชื่อลูกค้า" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="สถานะ" name="booking_status">
                <Select options={BOOKING_STATUS.map((s) => ({ value: s, label: s }))} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Booking No." name="booking_no">
                <Input placeholder="เลขที่จองเรือ" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="ชื่อเรือ" name="vessel">
                <Input placeholder="เช่น MSC WIND II V.HO634A" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Agent" name="agent">
                <Input placeholder="เช่น EVERGREEN / MAC-NELS" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Freight" name="freight">
                <Select
                  allowClear
                  options={FREIGHT_TERMS.map((s) => ({ value: s, label: s }))}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item label="CY Date" name="cy_date">
                <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Load Date" name="load_date">
                <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="เวลาโหลด" name="load_time">
                <Input placeholder="เช่น 09.00 น." />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="RT Date" name="rt_date">
                <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item label="ETD" name="etd">
                <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="ETA" name="eta">
                <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                label="จำนวนตู้ (FCL)"
                name="fcl"
                tooltip="กดปุ่มคำนวณเพื่อคิดจากมาตรฐานกล่อง/ตู้ของสินค้า หรือพิมพ์เองได้"
              >
                <InputNumber min={0} controls={false} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="ประเภทตู้" name="container_type">
                <Select
                  allowClear
                  options={CONTAINER_TYPES.map((s) => ({ value: s, label: s }))}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item label="หัวลาก" name="trailer">
                <Input placeholder="เช่น CPRO" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="ที่ลากตู้ / ที่บรรจุ" name="pickup_place">
                <Input placeholder="เช่น ECD" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Closing Time" name="closing_time">
                <Input placeholder="เช่น 6/5 @ 16.00 น." />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="1st Return" name="first_return">
                <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item label="SI Cut off" name="si_cutoff">
                <Input placeholder="เช่น 30/4 @ 17.00" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="VGM Cut off" name="vgm_cutoff">
                <Input placeholder="เช่น 1/5 @ 12.00" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="หมายเหตุ" name="remark">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      <Card
        title="Job Order / Invoice ที่โหลดรอบนี้"
        extra={
          isView ? null : (
            <Space>
              <Button
                size="small"
                icon={<CalculatorOutlined />}
                onClick={handleEstimateFcl}
              >
                คำนวณจำนวนตู้
              </Button>
              <Button
                size="small"
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setOpenJo(true)}
              >
                เพิ่ม Job Order
              </Button>
            </Space>
          )
        }
      >
        <Table
          size="small"
          bordered
          rowKey={(r, i) => `${r.jocode}-${i}`}
          dataSource={jobs}
          pagination={false}
          locale={{ emptyText: <span>ยังไม่ได้เลือก Job Order</span> }}
          columns={[
            { title: "Job No.", dataIndex: "jocode", width: 120 },
            { title: "ลูกค้า", dataIndex: "cusname", ellipsis: true },
            { title: "Brand", dataIndex: "brand", width: 150, ellipsis: true },
            {
              title: "เลขที่ Invoice",
              dataIndex: "inv_code",
              width: 170,
              render: (v, _r, i) => (
                <Input
                  size="small"
                  value={v}
                  disabled={isView}
                  placeholder="เว้นว่างได้"
                  onChange={(e) => updateJob(i, "inv_code", e.target.value)}
                />
              ),
            },
            {
              title: "จำนวนตู้",
              dataIndex: "fcl",
              width: 110,
              align: "right",
              render: (v, _r, i) => (
                <InputNumber
                  size="small"
                  min={0}
                  controls={false}
                  value={v}
                  disabled={isView}
                  style={{ width: "100%" }}
                  onChange={(val) => updateJob(i, "fcl", val)}
                />
              ),
            },
            ...(isView
              ? []
              : [
                  {
                    title: "",
                    width: 60,
                    align: "center",
                    render: (_v, _r, i) => (
                      <Popconfirm
                        title="เอาออกจากรอบโหลดนี้?"
                        okText="เอาออก"
                        cancelText="ยกเลิก"
                        onConfirm={() => removeJob(i)}
                      >
                        <Button size="small" danger icon={<RiDeleteBin5Line />} />
                      </Popconfirm>
                    ),
                  },
                ]),
          ]}
        />
      </Card>

      {openJo && (
        <ModalJobOrder
          show={openJo}
          close={() => setOpenJo(false)}
          selected={handleChooseJo}
        />
      )}
    </Space>
  );
}
